import { db } from '@/lib/firebase';
import { collection, getDocs, getDoc, doc, query, where, orderBy, limit, updateDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import type { Item, ItemStatus, ItemType, PricingModel } from '@/types/marketplace';

export type { ItemStatus, ItemType, PricingModel };

export interface NewListingData {
  title: string;
  description: string;
  categoryKey: string;
  type: ItemType;
  pricingModel: PricingModel;
  currency: string;
  price?: number;
  minPrice?: number;
  tags: string[];
  status: ItemStatus;
  media: { order: number; url: string; type: 'image' | 'video' | 'audio'; label?: string }[];
  seller: { userId: string; organizationId?: string; organizationName?: string };
  inventory?: { trackQuantity: boolean; quantityAvailable?: number };
  service?: { duration?: number; deliveryTime?: number; requiresBooking?: boolean };
  digital?: { formats: string[]; licenseType?: string };
}

/**
 * Fetches marketplace items from the "item" collection.
 * @param options.includeHidden  If true, includes adminHidden items (for admins only).
 */
export async function getMarketplaceItems(options?: { includeHidden?: boolean }): Promise<Item[]> {
  try {
    const itemsCollection = collection(db, 'item');
    const q = query(itemsCollection, where('status', '==', 'active'));
    const querySnapshot = await getDocs(q);

    const items = querySnapshot.docs.map(doc => ({
      itemId: doc.id,
      ...doc.data(),
    } as Item));

    // Filter out adminHidden items unless the caller is an admin
    if (!options?.includeHidden) {
      return items.filter(item => !item.adminHidden);
    }
    return items;
  } catch (error) {
    console.error("Error fetching marketplace items from Firestore:", error);
    return [];
  }
}

/**
 * Toggle the adminHidden flag on a marketplace item.
 */
export async function toggleMarketplaceItemHidden(itemId: string, hidden: boolean): Promise<void> {
  const docRef = doc(db, 'item', itemId);
  await updateDoc(docRef, { adminHidden: hidden, updatedAt: serverTimestamp() });
}

/**
 * Fetch a single marketplace item by ID
 */
export async function getMarketplaceItemById(itemId: string): Promise<Item | null> {
  try {
    const docRef = doc(db, 'item', itemId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { itemId: docSnap.id, ...docSnap.data() } as Item;
  } catch (error) {
    console.error('Error fetching marketplace item:', error);
    return null;
  }
}

/**
 * Fetch marketplace items by category
 */
export async function getMarketplaceItemsByCategory(categoryKey: string): Promise<Item[]> {
  try {
    const q = query(
      collection(db, 'item'),
      where('status', '==', 'active'),
      where('categoryKey', '==', categoryKey)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .filter(doc => !doc.data().adminHidden)
      .map(doc => ({
        itemId: doc.id,
        ...doc.data(),
      })) as Item[];
  } catch (error) {
    console.error('Error fetching items by category:', error);
    return [];
  }
}

/**
 * Fetch marketplace items by type (e.g., 'pod', 'merch', 'physical_media')
 */
export async function getMarketplaceItemsByType(type: string): Promise<Item[]> {
  try {
    const q = query(
      collection(db, 'item'),
      where('status', '==', 'active'),
      where('type', '==', type)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .filter(doc => !doc.data().adminHidden)
      .map(doc => ({
        itemId: doc.id,
        ...doc.data(),
      })) as Item[];
  } catch (error) {
    console.error('Error fetching items by type:', error);
    return [];
  }
}

/**
 * Create a new marketplace listing
 */
export async function addMarketplaceItem(data: NewListingData): Promise<string> {
  const docRef = await addDoc(collection(db, 'item'), {
    ...data,
    inventory: data.inventory ?? { trackQuantity: false },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Delete a marketplace item by ID
 */
export async function deleteMarketplaceItem(itemId: string): Promise<void> {
  await deleteDoc(doc(db, 'item', itemId));
}

/**
 * Update a marketplace item by ID
 */
export async function updateMarketplaceItem(itemId: string, updates: Record<string, any>): Promise<void> {
  try {
    const docRef = doc(db, 'item', itemId);
    // Remove undefined values
    const cleanUpdates: Record<string, any> = {};
    for (const [key, value] of Object.entries(updates)) {
      if (value !== undefined) cleanUpdates[key] = value;
    }
    cleanUpdates.updatedAt = serverTimestamp();
    await updateDoc(docRef, cleanUpdates);
  } catch (error) {
    console.error('Error updating marketplace item:', error);
    throw error;
  }
}
